import React, { useRef, useState } from 'react';
import { Alert, Button, Col, Container, Form, Row } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import {
    usePreviewLock,
    PREVIEW_LOCK_NOTE_ID,
} from '@/shared/hooks/usePreviewLock';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import {
    TeamEmailLink,
    withTeamEmailLink,
} from '@/components/common/TeamEmailLink';
import { TEAM_EMAIL } from '@/shared/utils/contact';
import feedbackService from '@/shared/services/feedbackService';
import { pageFromSearch } from './feedbackLink';

const CATEGORIES = [
    { value: 'problem', label: 'Problem' },
    { value: 'suggestion', label: 'Suggestion' },
    { value: 'question', label: 'Question' },
];

const MAX_SUMMARY = 120;
const MAX_DETAILS = 5000;
const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// Reports go to the development team's issue tracker, so student records do
// not belong in them.
const STUDENT_INFO_NOTICE =
    "Please don't include student names, IDs or other student information.";

const SERVER_FALLBACK = `We couldn't send your feedback. Please try again, or email the Peer Support Hub team at ${TEAM_EMAIL}.`;

// Field order is the order errors are listed in and the order the first
// invalid field is chosen for focus.
const FIELD_IDS = {
    category: 'feedback-category',
    summary: 'feedback-summary',
    details: 'feedback-details',
    screenshot: 'feedback-screenshot',
};

function validate({ category, summary, details, screenshot }) {
    const errors = {};
    if (!category) {
        errors.category = 'Choose a category.';
    }
    if (!summary.trim()) {
        errors.summary = 'Enter a short summary.';
    } else if (summary.trim().length > MAX_SUMMARY) {
        errors.summary = `Keep the summary to ${MAX_SUMMARY} characters or fewer.`;
    }
    if (!details.trim()) {
        errors.details = 'Enter the details.';
    } else if (details.trim().length > MAX_DETAILS) {
        errors.details = `Keep the details to ${MAX_DETAILS} characters or fewer.`;
    }
    if (screenshot) {
        if (!IMAGE_TYPES.includes(screenshot.type)) {
            errors.screenshot =
                'The screenshot must be a JPEG, PNG, GIF, or WebP image.';
        } else if (screenshot.size > MAX_SCREENSHOT_BYTES) {
            errors.screenshot = 'The screenshot must be 5 MB or smaller.';
        }
    }
    return errors;
}

function FeedbackPage() {
    const location = useLocation();
    const previewLocked = usePreviewLock();
    // The navbar and footer links pass where the person was; a direct visit
    // has none, and the form says so rather than guessing.
    const from = pageFromSearch(location.search);

    const [values, setValues] = useState({
        category: '',
        summary: '',
        details: '',
        screenshot: null,
    });
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const errorSummaryRef = useRef(null);
    const serverErrorRef = useRef(null);
    const successRef = useRef(null);

    const setField = (name, value) =>
        setValues((current) => ({ ...current, [name]: value }));

    const handleSubmit = async (event) => {
        event.preventDefault();
        setServerError('');

        const found = validate(values);
        setErrors(found);
        if (Object.keys(found).length > 0) {
            // Focus lands on the summary of problems, which is announced as
            // an alert; the links in it move focus to each field.
            setTimeout(() => errorSummaryRef.current?.focus(), 0);
            return;
        }

        setSubmitting(true);
        try {
            const data = await feedbackService.submitFeedback({
                category: values.category,
                summary: values.summary.trim(),
                details: values.details.trim(),
                page: from,
                screenshot: values.screenshot,
            });
            setResult(data);
            setTimeout(() => successRef.current?.focus(), 0);
        } catch (err) {
            setServerError(err.response?.data?.message || SERVER_FALLBACK);
            setTimeout(() => serverErrorRef.current?.focus(), 0);
        } finally {
            setSubmitting(false);
        }
    };

    const focusField = (name) => (event) => {
        event.preventDefault();
        document.getElementById(FIELD_IDS[name])?.focus();
    };

    if (result) {
        return (
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col lg={8} xl={6}>
                        <div role="status">
                            <h1
                                className="h3 mb-3"
                                tabIndex={-1}
                                ref={successRef}
                            >
                                Thank you for your feedback
                            </h1>
                            <p>{result.message}</p>
                            {result.screenshotStored === false && (
                                <p>
                                    Your screenshot could not be attached, but
                                    your feedback was sent.
                                </p>
                            )}
                        </div>
                        <Link
                            to={from || '/dashboard'}
                            className="btn btn-primary"
                        >
                            {from
                                ? 'Back to where you were'
                                : 'Go to dashboard'}
                        </Link>
                    </Col>
                </Row>
            </Container>
        );
    }

    const errorFields = Object.keys(FIELD_IDS).filter((name) => errors[name]);

    return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col lg={8} xl={6}>
                    <h1 className="h3 mb-2">Send feedback</h1>
                    <p className="text-muted mb-4">
                        Tell the Peer Support Hub team about a problem, a
                        suggestion, or a question. Your name and email are sent
                        with it so the team can reply. You can also email{' '}
                        <TeamEmailLink />.
                    </p>

                    {errorFields.length > 0 && (
                        <Alert
                            variant="danger"
                            role="alert"
                            tabIndex={-1}
                            ref={errorSummaryRef}
                        >
                            <p className="fw-bold mb-1">
                                Please fix the following:
                            </p>
                            <ul className="mb-0">
                                {errorFields.map((name) => (
                                    <li key={name}>
                                        <a
                                            href={`#${FIELD_IDS[name]}`}
                                            onClick={focusField(name)}
                                        >
                                            {errors[name]}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    {serverError && (
                        <Alert
                            variant="danger"
                            role="alert"
                            tabIndex={-1}
                            ref={serverErrorRef}
                        >
                            {/email the Peer Support Hub team\.$/i.test(
                                serverError
                            ) ? (
                                <>
                                    {serverError.slice(0, -1)} at{' '}
                                    <TeamEmailLink />.
                                </>
                            ) : (
                                withTeamEmailLink(serverError)
                            )}
                        </Alert>
                    )}

                    <Form noValidate onSubmit={handleSubmit}>
                        <Form.Group
                            className="mb-3"
                            controlId="feedback-category"
                        >
                            <Form.Label>Category (required)</Form.Label>
                            <Form.Select
                                value={values.category}
                                isInvalid={!!errors.category}
                                aria-invalid={!!errors.category}
                                aria-describedby={
                                    errors.category
                                        ? 'feedback-category-error'
                                        : undefined
                                }
                                onChange={(e) =>
                                    setField('category', e.target.value)
                                }
                            >
                                <option value="">Select a category</option>
                                {CATEGORIES.map((category) => (
                                    <option
                                        key={category.value}
                                        value={category.value}
                                    >
                                        {category.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback
                                type="invalid"
                                id="feedback-category-error"
                            >
                                {errors.category}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group
                            className="mb-3"
                            controlId="feedback-summary"
                        >
                            <Form.Label>Short summary (required)</Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={MAX_SUMMARY}
                                value={values.summary}
                                isInvalid={!!errors.summary}
                                aria-invalid={!!errors.summary}
                                aria-describedby={
                                    errors.summary
                                        ? 'feedback-summary-error'
                                        : undefined
                                }
                                onChange={(e) =>
                                    setField('summary', e.target.value)
                                }
                            />
                            <Form.Control.Feedback
                                type="invalid"
                                id="feedback-summary-error"
                            >
                                {errors.summary}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group
                            className="mb-3"
                            controlId="feedback-details"
                        >
                            <Form.Label>Details (required)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                maxLength={MAX_DETAILS}
                                value={values.details}
                                isInvalid={!!errors.details}
                                aria-invalid={!!errors.details}
                                aria-describedby={
                                    errors.details
                                        ? 'feedback-details-help feedback-details-error'
                                        : 'feedback-details-help'
                                }
                                onChange={(e) =>
                                    setField('details', e.target.value)
                                }
                            />
                            <Form.Text id="feedback-details-help" muted>
                                {STUDENT_INFO_NOTICE}
                            </Form.Text>
                            <Form.Control.Feedback
                                type="invalid"
                                id="feedback-details-error"
                            >
                                {errors.details}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group
                            className="mb-3"
                            controlId="feedback-screenshot"
                        >
                            <Form.Label>Screenshot (optional)</Form.Label>
                            <Form.Control
                                type="file"
                                accept={IMAGE_TYPES.join(',')}
                                isInvalid={!!errors.screenshot}
                                aria-invalid={!!errors.screenshot}
                                aria-describedby={
                                    errors.screenshot
                                        ? 'feedback-screenshot-help feedback-screenshot-error'
                                        : 'feedback-screenshot-help'
                                }
                                onChange={(e) =>
                                    setField(
                                        'screenshot',
                                        e.target.files?.[0] || null
                                    )
                                }
                            />
                            <Form.Text id="feedback-screenshot-help" muted>
                                JPEG, PNG, GIF, or WebP, up to 5 MB.{' '}
                                {STUDENT_INFO_NOTICE}
                            </Form.Text>
                            <Form.Control.Feedback
                                type="invalid"
                                id="feedback-screenshot-error"
                            >
                                {errors.screenshot}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <p className="text-muted small">
                            {from ? (
                                <>
                                    Page you were on: <code>{from}</code>
                                </>
                            ) : (
                                'This feedback is not tied to a page.'
                            )}
                        </p>

                        <Button
                            type="submit"
                            variant="primary"
                            disabled={submitting || previewLocked}
                            aria-describedby={
                                previewLocked ? PREVIEW_LOCK_NOTE_ID : undefined
                            }
                        >
                            {submitting ? 'Sending...' : 'Send feedback'}
                        </Button>
                        <PreviewLockNote locked={previewLocked} />
                    </Form>
                </Col>
            </Row>
        </Container>
    );
}

export default FeedbackPage;
